import React, { useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  getOrCreateGlobalContext,
  CONTEXT_NAMES
} from '@adsk/bim360-matrix-react-helpers/es/globalContexts';
import { fetchToken } from '../services/fetchToken';
import { useEnvConfig } from './env';
import { ENV_CONFIG_KEYS } from '../helpers/envConsts';
import redirectToLogin from '../helpers/redirectToLogin';

const TokenContext = getOrCreateGlobalContext(CONTEXT_NAMES.TokenContext);

export const TokenProvider = ({
  onTokenExpired: onTokenExpiredFromProps,
  children,
  getToken: getTokenFromProps
}) => {
  const latestPromiseRef = useRef(null);
  const tokenRef = useRef(null);
  const onTokenExpiredRef = useRef(onTokenExpiredFromProps);
  const getTokenFromPropsRef = useRef(getTokenFromProps);
  const docsUrl = useEnvConfig(ENV_CONFIG_KEYS.DOCS);

  useEffect(() => {
    onTokenExpiredRef.current = onTokenExpiredFromProps;
  }, [onTokenExpiredFromProps]);

  useEffect(() => {
    getTokenFromPropsRef.current = getTokenFromProps;
  }, [getTokenFromProps]);

  const onTokenExpired = useCallback(() => {
    onTokenExpiredRef.current ? onTokenExpiredRef.current() : redirectToLogin(docsUrl);
  }, [docsUrl]);

  const getToken = useCallback(() => {
    if (getTokenFromPropsRef.current) {
      return getTokenFromPropsRef.current();
    }
    if (latestPromiseRef.current) {
      return latestPromiseRef.current;
    }
    // in case this request was made in the last 30 seconds of the token to expire we want to get a new one.
    if (!tokenRef.current || tokenRef.current.expirationTime - Date.now() <= 30 * 1000) {
      latestPromiseRef.current = fetchToken(docsUrl)
        .then(token => {
          latestPromiseRef.current = null;
          tokenRef.current = token;
          return token;
        })
        .then(token => token.token)
        .catch(e => {
          latestPromiseRef.current = null;
          if (e.status && [401, 403].includes(e.status)) {
            onTokenExpired();
          }
          throw e;
        });
      return latestPromiseRef.current;
    }

    return Promise.resolve(tokenRef.current.token);
  }, [docsUrl, onTokenExpired]);

  return (
    <TokenContext.Provider value={{ getToken, onTokenExpired }}>{children}</TokenContext.Provider>
  );
};

TokenProvider.propTypes = {
  /**
   * A method resolved to an active token. If not passed - DOCS token handling would be used.
   */
  getToken: PropTypes.func,
  /**
   * Method to redirect user to login when token is expired. If not passed - user would redirect for DOCS to login.
   */
  onTokenExpired: PropTypes.func,
  children: PropTypes.node.isRequired
};

export const useToken = () => useContext(TokenContext);

export default TokenContext;
